import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { adminApi, fmtErr } from "../api";
import { AdminAuthShell } from "../components";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/auth/reset-password", { token, new_password: form.new_password });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminAuthShell>
      <div className="bg-white p-8 rounded-sm shadow-2xl" data-testid="reset-password-form">
        <h1 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={16} className="text-[#C5221F]" /> Set New Password</h1>
        {done ? (
          <div className="mt-5">
            <p className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed bg-emerald-50 border border-emerald-200 px-4 py-3.5 rounded-sm" data-testid="reset-success-msg">
              <CheckCircle2 size={17} className="text-emerald-600 shrink-0 mt-0.5" />
              Password reset successfully. Please sign in with your new password.
            </p>
            <Link to="/admin" className="mt-5 w-full inline-flex justify-center bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150" data-testid="reset-goto-login">
              Go to Sign In
            </Link>
          </div>
        ) : !token ? (
          <p className="mt-5 text-sm text-slate-600 bg-red-50 border border-red-200 px-4 py-3.5 rounded-sm" data-testid="reset-no-token">
            This reset link is invalid. Please request a new one from the sign-in page.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="rp-new" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">New Password</label>
              <input id="rp-new" type="password" required minLength={10} autoComplete="new-password" data-testid="reset-new-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
              <p className="mt-1 text-[11px] text-slate-400">Minimum 10 characters.</p>
            </div>
            <div>
              <label htmlFor="rp-confirm" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Confirm New Password</label>
              <input id="rp-confirm" type="password" required minLength={10} autoComplete="new-password" data-testid="reset-confirm-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
            {error && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="reset-submit-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Reset Password
            </button>
          </form>
        )}
        <Link to="/admin" className="mt-5 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F] transition-colors" data-testid="reset-back-link">
          <ArrowLeft size={12} /> Back to Sign In
        </Link>
      </div>
    </AdminAuthShell>
  );
};

export default ResetPassword;
